import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { isValidString, getAccessToken } from '../common/CommonUtilsMethods';
import { configureWorkyAmplify } from './WorkyAmplifyConfig';
import {
    getExistingCognitoAuthSession,
    signInWithEmailPassword,
    signOutCognitoSession,
} from './WorkyCognitoAuthService';
import type {
    CognitoAuthTokensResult,
    CognitoExistingSessionResult,
} from './WorkyCognitoAuthServiceInterfaces';
import { handleCognitoPasswordSignInOutcome } from './WorkyCustomAuthSignInOutcomeService';
import { completeCustomSignIn } from './WorkyTokenSignInService';
import { navigateAfterSignIn } from './WorkyPostLoginNavigationService';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { navigateToSignUp } from './WorkyAuthNavigationService';
import {
    WORKY_SUPPORT_EMAIL,
    WORKY_SUPPORT_PHONE_DIGITS,
    WORKY_SUPPORT_PHONE_DISPLAY,
} from '../common/WorkySupportContactConstants';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import type { WorkySignInResponse } from '../remoteExecution/RemoteExecutionServiceImpl';
import {
    buildWorkyAuthPathWithEmail,
    buildWorkySignUpPathWithEmailAndCorpId,
    readPrimaryWorkyCorpIdFromLocationSearch,
    readWorkyAuthEmailFromLocationSearch,
} from './WorkyAuthQueryParamService';
import { resolveRegistrationInvite } from './WorkyRegistrationResolveInviteService';
import { RegistrationMatchType } from './WorkyRegistrationInterfaces';
import type { RegistrationInviteLookupResponseDto } from './WorkyRegistrationInterfaces';
import { LoginWizardStep } from './WorkyLoginPageInterfaces';
import { landingPageSeoByPath } from '../landing-page/seo/LandingPageSeoConfig';
import { useLandingPageSeo } from '../landing-page/seo/useLandingPageSeo';
import './WorkyLoginPage.css';

const WorkyLoginPage = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const hasAttemptedSessionResumeRef = useRef<boolean>(false);

    useLandingPageSeo(landingPageSeoByPath[WORKY_WEB_APP_PATHS.login]);

    const [currentStep, setCurrentStep] = useState<LoginWizardStep>(LoginWizardStep.EMAIL);
    const [emailAddress, setEmailAddress] = useState<string>(
        readWorkyAuthEmailFromLocationSearch(location.search)
    );
    const [password, setPassword] = useState<string>('');
    const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isResumingSession, setIsResumingSession] = useState<boolean>(true);
    const [cognitoSignedInEmailAddress, setCognitoSignedInEmailAddress] = useState<string | null>(null);

    const bridgeCognitoTokensToWorkySession = useCallback(
        async (cognitoTokens: CognitoAuthTokensResult): Promise<WorkySignInResponse | null> => {
            const primaryWorkyCorpId: string | null = readPrimaryWorkyCorpIdFromLocationSearch(
                location.search
            );

            return completeCustomSignIn({
                cognitoAccessToken: cognitoTokens.accessToken,
                cognitoIdToken: cognitoTokens.idToken,
                cognitoRefreshToken: cognitoTokens.refreshToken,
                expiresInSeconds: cognitoTokens.expiresInSeconds,
                primaryWorkyCorpId,
            });
        },
        [location.search]
    );

    useEffect(() => {
        configureWorkyAmplify();

        const existingAccessToken: string | null = getAccessToken();
        if (isValidString(existingAccessToken)) {
            history.replace(WORKY_WEB_APP_PATHS.homepage);
            return;
        }

        if (hasAttemptedSessionResumeRef.current) {
            return;
        }
        hasAttemptedSessionResumeRef.current = true;

        const resumeExistingCognitoSession = async (): Promise<void> => {
            setIsResumingSession(true);
            try {
                const existingSession: CognitoExistingSessionResult | null =
                    await getExistingCognitoAuthSession();

                if (existingSession === null) {
                    setCognitoSignedInEmailAddress(null);
                    return;
                }

                setCognitoSignedInEmailAddress(existingSession.signedInEmailAddress);
                setEmailAddress(existingSession.signedInEmailAddress);
                setCurrentStep(LoginWizardStep.PASSWORD);

                const signInResponse = await bridgeCognitoTokensToWorkySession(existingSession.tokens);
                if (signInResponse === null) {
                    enqueueSnackbar(
                        'Could not finish signing in to Worky. Enter your password to try again.',
                        { variant: 'warning' }
                    );
                    return;
                }

                enqueueSnackbar('Signed in successfully.', { variant: 'success' });
                navigateAfterSignIn({
                    history,
                    userType: signInResponse.basicJobberDetails.userType,
                    jobberStatus: signInResponse.basicJobberDetails.jobberStatus,
                });
            } finally {
                setIsResumingSession(false);
            }
        };

        void resumeExistingCognitoSession();
    }, [bridgeCognitoTokensToWorkySession, enqueueSnackbar, history]);

    const handleSubmitPassword = useCallback(
        async (submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> => {
            submitEvent.preventDefault();

            if (isSubmitting || isResumingSession) {
                return;
            }

            setIsSubmitting(true);

            try {
                const signInOutcome = await signInWithEmailPassword({
                    emailAddress,
                    password,
                });

                await handleCognitoPasswordSignInOutcome({
                    signInOutcome,
                    history,
                    enqueueSnackbar,
                    primaryWorkyCorpId: readPrimaryWorkyCorpIdFromLocationSearch(location.search),
                    successMessage: 'Signed in successfully.',
                });
            } catch (error: unknown) {
                console.error('Unexpected error during custom login', error);
                enqueueSnackbar('Sign-in failed. Please try again.', { variant: 'error' });
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            emailAddress,
            enqueueSnackbar,
            history,
            isResumingSession,
            isSubmitting,
            location.search,
            password,
        ]
    );

    const handleEmailStepContinue = useCallback(async (): Promise<void> => {
        if (isSubmitting || isResumingSession) {
            return;
        }

        const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
        if (!isValidString(normalizedEmailAddress)) {
            enqueueSnackbar('Please enter your email address.', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);
        setBlockedMessage(null);

        const primaryWorkyCorpIdFromQuery: string | null = readPrimaryWorkyCorpIdFromLocationSearch(
            location.search
        );

        try {
            const lookupResponse: RegistrationInviteLookupResponseDto | null =
                await resolveRegistrationInvite({
                    jobberEmail: normalizedEmailAddress,
                    primaryWorkyCorpId: primaryWorkyCorpIdFromQuery,
                });

            if (lookupResponse === null) {
                enqueueSnackbar('Unable to verify your email. Please try again.', { variant: 'error' });
                return;
            }

            setEmailAddress(normalizedEmailAddress);

            if (lookupResponse.matchType === RegistrationMatchType.ALREADY_REGISTERED) {
                setCurrentStep(LoginWizardStep.PASSWORD);
                return;
            }

            if (lookupResponse.canProceed) {
                enqueueSnackbar('No account found for this email. Continue to sign up.', {
                    variant: 'info',
                });
                history.push(
                    buildWorkySignUpPathWithEmailAndCorpId(
                        normalizedEmailAddress,
                        lookupResponse.primaryWorkyCorpId ?? primaryWorkyCorpIdFromQuery
                    )
                );
                return;
            }

            setBlockedMessage(
                lookupResponse.blockedMessage ??
                    'You have not been invited by a client on Worky, please reach out to your organization and request them to invite you'
            );
            setCurrentStep(LoginWizardStep.BLOCKED);
        } finally {
            setIsSubmitting(false);
        }
    }, [emailAddress, enqueueSnackbar, history, isResumingSession, isSubmitting, location.search]);

    const handleUseDifferentAccountClick = useCallback(async (): Promise<void> => {
        setIsSubmitting(true);
        try {
            await signOutCognitoSession();
            setCognitoSignedInEmailAddress(null);
            setEmailAddress('');
            setPassword('');
            setBlockedMessage(null);
            setCurrentStep(LoginWizardStep.EMAIL);
            enqueueSnackbar('Signed out. Enter a different account.', { variant: 'info' });
        } finally {
            setIsSubmitting(false);
        }
    }, [enqueueSnackbar]);

    const handleUseDifferentEmailClick = useCallback((): void => {
        setPassword('');
        setBlockedMessage(null);
        setCurrentStep(LoginWizardStep.EMAIL);
    }, []);

    const handleSignUpClick = useCallback((): void => {
        const primaryWorkyCorpId: string | null = readPrimaryWorkyCorpIdFromLocationSearch(location.search);
        void navigateToSignUp(primaryWorkyCorpId ?? undefined);
    }, [location.search]);

    const handleForgotPasswordClick = useCallback((): void => {
        history.push(
            isValidString(emailAddress)
                ? buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.forgotPassword, emailAddress)
                : WORKY_WEB_APP_PATHS.forgotPassword
        );
    }, [emailAddress, history]);

    const handleNavigateHomeClick = useCallback((): void => {
        history.push(WORKY_WEB_APP_PATHS.home);
    }, [history]);

    const renderStepContent = (): JSX.Element => {
        if (currentStep === LoginWizardStep.EMAIL) {
            return (
                <>
                    <p className="worky-login-subtitle">Enter the email address you were invited with.</p>
                    <form
                        className="worky-login-form"
                        onSubmit={(submitEvent: React.FormEvent<HTMLFormElement>): void => {
                            submitEvent.preventDefault();
                            void handleEmailStepContinue();
                        }}
                    >
                        <TextField
                            label="Email"
                            type="email"
                            autoComplete="email"
                            value={emailAddress}
                            onChange={(changeEvent): void => setEmailAddress(changeEvent.target.value)}
                            required
                            fullWidth
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="worky-login-submit-button"
                            disabled={isSubmitting}
                            fullWidth
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
                        </Button>
                    </form>
                </>
            );
        }

        if (currentStep === LoginWizardStep.BLOCKED && blockedMessage !== null) {
            return (
                <>
                    <p className="worky-login-subtitle">{blockedMessage}</p>
                    <Button
                        type="button"
                        variant="outlined"
                        fullWidth
                        onClick={handleUseDifferentEmailClick}
                    >
                        Try a different email
                    </Button>
                </>
            );
        }

        return (
            <>
                <p className="worky-login-subtitle">
                    Sign in to Worky Pro with your Worky account email and password.
                </p>

                {isValidString(cognitoSignedInEmailAddress) ? (
                    <div className="worky-login-session-banner">
                        <span>
                            Continuing as <strong>{cognitoSignedInEmailAddress}</strong>
                        </span>
                        <button
                            type="button"
                            className="worky-login-session-switch-button"
                            onClick={(): void => {
                                void handleUseDifferentAccountClick();
                            }}
                            disabled={isSubmitting}
                        >
                            Use a different account
                        </button>
                    </div>
                ) : null}

                <form className="worky-login-form" onSubmit={handleSubmitPassword}>
                    <TextField
                        label="Email"
                        type="email"
                        autoComplete="email"
                        value={emailAddress}
                        required
                        fullWidth
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(changeEvent): void => setPassword(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="worky-login-submit-button"
                        disabled={isSubmitting}
                        fullWidth
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
                    </Button>
                    <Button
                        type="button"
                        variant="text"
                        disabled={isSubmitting}
                        fullWidth
                        onClick={handleForgotPasswordClick}
                    >
                        Forgot password?
                    </Button>
                    {!isValidString(cognitoSignedInEmailAddress) ? (
                        <Button
                            type="button"
                            variant="text"
                            disabled={isSubmitting}
                            fullWidth
                            onClick={handleUseDifferentEmailClick}
                        >
                            Use a different email
                        </Button>
                    ) : null}
                </form>
            </>
        );
    };

    if (isResumingSession) {
        return (
            <div className="worky-login-page">
                <div className="worky-login-card worky-login-card-loading">
                    <CircularProgress size={32} />
                    <p className="worky-login-subtitle">Checking your existing session…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleNavigateHomeClick}
            >
                <ArrowBackIcon fontSize="small" />
                Back to home
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateHomeClick}
                    aria-label="Worky home"
                >
                    <img
                        src={WORKY_BRAND_LOGO_SRC}
                        alt="Worky"
                        className="worky-login-logo-image"
                    />
                </button>

                <h1 className="worky-login-title">Worky Login</h1>
                {renderStepContent()}

                <div className="worky-login-footer">
                    Need an account?{' '}
                    <span className="worky-login-footer-link" onClick={handleSignUpClick} role="button" tabIndex={0}>
                        Sign up
                    </span>
                </div>

                <div className="worky-login-support-footer">
                    <span className="worky-login-support-label">Need help? Contact Us</span>{' '}
                    <a className="worky-login-support-link" href={`mailto:${WORKY_SUPPORT_EMAIL}`}>
                        {WORKY_SUPPORT_EMAIL}
                    </a>
                    {' · '}
                    <a className="worky-login-support-link" href={`tel:${WORKY_SUPPORT_PHONE_DIGITS}`}>
                        {WORKY_SUPPORT_PHONE_DISPLAY}
                    </a>
                </div>
            </div>
        </div>
    );
};

export { WorkyLoginPage };
